import { Box, type BoxProps, Flex, Text } from '@mantine/core'

import { type CoreContractPreview } from '@flecto/types'
import { TextCurrency } from '@flecto/ui'

import { useDocumentState } from '../../../booking-documents/hooks/useDocumentState'
import { getTaxBracket } from '../../../booking-documents/services/getTaxBracket'
import { useServiceKind } from '../../../booking-services/hooks/useServiceKind'
import { SectionTable } from './SectionTable'

type SectionServicesProps = BoxProps

export function SectionServices({ ...props }: SectionServicesProps) {
  const { documentPreview, taxes, translate } = useDocumentState()

  const kinds = useServiceKind()

  if (!documentPreview?.booking_services?.length) {
    return null
  }

  const showTaxes = documentPreview?.owner?.show_prices_with_tax ?? false

  const lastColumnSize = showTaxes ? '30%' : '40%'
  const totalServicesWithTaxes =
    documentPreview?.booking?.services_total_with_vat_cents ?? 0
  const totalServicesWithoutTaxes =
    documentPreview?.booking?.services_total_cents ?? 0

  const renderBookingServices = (
    bookingService: CoreContractPreview['booking_services']
  ) =>
    bookingService?.map((s, i) => {
      const kind = kinds.find((k) => k.value === s.service_kind)
      return (
        <Box key={i} mb='sm'>
          <Flex justify='space-between' mt='xs'>
            <Box w='60%'>
              <Text fw={500} size='md'>
                {kind?.label}
              </Text>
            </Box>
            {showTaxes && (
              <Box w='10%'>
                <Text c='gray.7' size='md'>
                  {getTaxBracket(String(s.tax_rate_percent), taxes)?.percentage}
                  %
                </Text>
              </Box>
            )}
            <Flex
              display='flex'
              align='flex-end'
              justify='flex-start'
              w={lastColumnSize}
              direction='column'
            >
              <TextCurrency cents={s.price_cents} size='md' />
            </Flex>
          </Flex>
        </Box>
      )
    })

  const bookingServices = documentPreview?.booking_services

  return (
    documentPreview?.booking_settings
      ?.documents_contract_include_rental_details && (
      <SectionTable {...props}>
        <SectionTable.Header>
          <Text w='60%' fw={500} size='md'>
            {translate?.('label_service')}
          </Text>
          {showTaxes && (
            <Text w='10%' fw={500} size='md'>
              {translate?.('label_tax_symbol')}
            </Text>
          )}
          <Text w={lastColumnSize} ta='right' fw={500} size='md'>
            {translate?.(
              showTaxes ? 'label_price_with_taxes' : 'label_price_without_taxes'
            )}
          </Text>
        </SectionTable.Header>
        <SectionTable.Body>
          {bookingServices && renderBookingServices(bookingServices)}
        </SectionTable.Body>
        <SectionTable.Footer>
          <Flex gap='xs'>
            <Text fw={500} size='md'>
              {translate?.(
                showTaxes
                  ? 'label_subtotal_with_taxes'
                  : 'label_subtotal_without_taxes'
              )}
              :
            </Text>
            <TextCurrency
              fw={500}
              cents={
                showTaxes ? totalServicesWithTaxes : totalServicesWithoutTaxes
              }
              size='md'
            />
          </Flex>
        </SectionTable.Footer>
      </SectionTable>
    )
  )
}
